import { useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material'
import { useIntl } from 'react-intl'
import { ConsignmentInfoPanel } from '../../../components/ConsignmentInfoPanel'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../components/Typographies/LabelItem'
import SideModalWrapper from '../../Assets/modals/SideModalWrapper'
import { messages } from '../messages'
import { UnassignedConsignment, UnassignedLoad, UnassignedLoadContents } from '../types'
import ContentDetailsSidePanel from './ContentDetailsSidePanel'
import {
  useGetUnassignedConsignmentLoadsQuery,
  useGetUnassignedLoadContentsQuery,
} from '../../../app/redux-fetch/apiOrders'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'

type ConsignmentContentsSidePanelProps = {
  open: boolean
  onClose: () => void
  consignment: UnassignedConsignment | null
  tenantId: number
}

const ConsignmentContentsSidePanel = ({
  open,
  onClose,
  consignment,
  tenantId,
}: ConsignmentContentsSidePanelProps) => {
  const { formatMessage } = useIntl()

  // Fetch loads for the consignment when panel is open
  const { data: loads, isLoading: isLoadingLoads } = useGetUnassignedConsignmentLoadsQuery(
    { tenantId, shipmentBookingId: consignment?.shipmentBookingId ?? 0 },
    { skip: !open || !consignment }
  )

  // Get the first load to determine display type and fetch contents
  const firstLoad = loads?.[0]
  const hasValue = firstLoad?.value !== null && firstLoad?.value !== undefined

  // Fetch contents for the first load (only if it's an itemized load)
  const { data: contents, isLoading: isLoadingContents } = useGetUnassignedLoadContentsQuery(
    { tenantId, loadId: firstLoad?.loadId ?? 0 },
    { skip: !open || !firstLoad || hasValue }
  )

  // Fetch countries from CORE to resolve country of origin names
  const { data: countries } = useGetCountriesQuery(undefined, { skip: !open || !firstLoad || hasValue })

  // Build a lookup map for country names by ID
  const countryLookup = useMemo((): Map<number, string> => {
    if (!countries) return new Map<number, string>()
    return new Map<number, string>(countries.map((c: { id: number; name: string }) => [c.id, c.name] as [number, string]))
  }, [countries])

  // Helper function to get country name from ID
  const getCountryName = (countryOfOriginId?: number, countryOfOriginName?: string): string => {
    if (countryOfOriginId && countryLookup.has(countryOfOriginId)) {
      const name = countryLookup.get(countryOfOriginId)
      if (name) return name
    }
    return countryOfOriginName ?? formatMessage(messages.notAvailable)
  }

  // State for content details side panel
  const [selectedContent, setSelectedContent] = useState<UnassignedLoadContents | null>(null)
  const [contentDetailsOpen, setContentDetailsOpen] = useState(false)

  const handleContentClick = (content: UnassignedLoadContents) => {
    setSelectedContent(content)
    setContentDetailsOpen(true)
  }

  const handleContentDetailsClose = () => {
    setContentDetailsOpen(false)
    setSelectedContent(null)
  }

  if (!consignment) return null

  const isLoading = isLoadingLoads || isLoadingContents
  const hasContents = contents && contents.length > 0

  // Calculate tracking number range for display
  const getTrackingNumberRange = (): string => {
    if (!loads || loads.length === 0) return formatMessage(messages.notAvailable)
    if (loads.length === 1) return loads[0].trackingNumber ?? formatMessage(messages.notAvailable)
    
    // Get first and last tracking numbers
    const trackingNumbers = loads
      .map((l) => l.trackingNumber)
      .filter((t): t is string => t !== undefined && t !== null)
      .sort()
    
    if (trackingNumbers.length === 0) return formatMessage(messages.notAvailable)
    if (trackingNumbers.length === 1) return trackingNumbers[0]
    
    // Extract the suffix numbers if they follow a pattern like T123456-1, T123456-2, etc.
    const first = trackingNumbers[0]
    const last = trackingNumbers[trackingNumbers.length - 1]
    
    // Try to find common prefix and show range
    const commonPrefix = first.split('-')[0]
    const firstSuffix = first.split('-')[1]
    const lastSuffix = last.split('-')[1]
    
    if (commonPrefix && firstSuffix && lastSuffix) {
      return `${commonPrefix}-(${firstSuffix} ... ${lastSuffix})`
    }
    
    return `${first} - ${last}`
  }

  return (
    <SideModalWrapper
      open={open}
      onClose={onClose}
      headerLabel={formatMessage(messages.contents)}
      withBottomBox={false}
      width="62.5%"
    >
      <Box sx={{ p: '32px' }}>
        <ConsignmentInfoPanel
          data={{
            shipperName: consignment.shipperName,
            loadTypeName: consignment.loadTypeName,
            description: consignment.description,
            currencyCode: consignment.currencyCode,
            trackingNumber: consignment.trackingNumber,
            customerReferenceNumber: consignment.referenceNumber,
          }}
          variant="sidePanel"
        />

        {isLoadingLoads ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                border: '1px solid #ccc',
                borderLeft: '4px solid #636363',
                backgroundColor: '#ffffff',
                borderRadius: '4px',
                padding: '16px',
                gap: '8px',
                marginBottom: '16px',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <LabelItem>{formatMessage(messages.loadInformation)}</LabelItem>
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.trackingNumber)}
                    value={getTrackingNumberRange()}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.numberOfLoads)}
                    value={loads?.length ?? consignment.unassignedLoadCount}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.loadType)}
                    value={firstLoad?.loadTypeName ?? consignment.loadTypeName ?? formatMessage(messages.notAvailable)}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.tempRange)}
                    value={firstLoad?.temperatureRange ?? formatMessage(messages.notAvailable)}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
              </Grid>
            </Box>

            {hasValue ? (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  border: '1px solid #ccc',
                  borderLeft: '4px solid #636363',
                  backgroundColor: '#ffffff',
                  borderRadius: '4px',
                  padding: '16px',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelItem>{formatMessage(messages.value)}</LabelItem>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoDisplayItem
                      label={formatMessage(messages.totalValue)}
                      value={firstLoad?.value?.toString() ?? formatMessage(messages.notAvailable)}
                      isSmallScreen={false}
                      allowOverlap={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoDisplayItem
                      label={formatMessage(messages.description)}
                      value={firstLoad?.description ?? formatMessage(messages.notAvailable)}
                      isSmallScreen={false}
                      allowOverlap={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoDisplayItem
                      label={formatMessage(messages.containsHazardousMaterials)}
                      value={firstLoad?.isHazardous ? formatMessage(messages.yes) : formatMessage(messages.no)}
                      isSmallScreen={false}
                      allowOverlap={true}
                    />
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {formatMessage(messages.contents)}
                </Typography>

                {isLoadingContents ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{formatMessage(messages.productDescription)}</TableCell>
                          <TableCell>{formatMessage(messages.quantity)}</TableCell>
                          <TableCell>{formatMessage(messages.unitPrice)}</TableCell>
                          <TableCell>{formatMessage(messages.total)}</TableCell>
                          <TableCell>{formatMessage(messages.countryOfOrigin)}</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hasContents ? (
                          contents?.map((content) => (
                            <TableRow key={content.id}>
                              <TableCell>{content.description}</TableCell>
                              <TableCell>{content.quantity}</TableCell>
                              <TableCell>
                                {content.unitPrice !== null && content.unitPrice !== undefined
                                  ? content.unitPrice.toFixed(2)
                                  : formatMessage(messages.notAvailable)}
                              </TableCell>
                              <TableCell>
                                {content.total !== null && content.total !== undefined
                                  ? content.total.toFixed(2)
                                  : formatMessage(messages.notAvailable)}
                              </TableCell>
                              <TableCell>
                                {getCountryName(content.countryOfOriginId, content.countryOfOriginName)}
                              </TableCell>
                              <TableCell>
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => handleContentClick(content)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  {formatMessage(messages.view)}
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              {formatMessage(messages.noContents)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </>
        )}
      </Box>
      <ContentDetailsSidePanel
        open={contentDetailsOpen}
        onClose={handleContentDetailsClose}
        content={selectedContent}
        countryName={getCountryName(selectedContent?.countryOfOriginId, selectedContent?.countryOfOriginName)}
      />
    </SideModalWrapper>
  )
}

export default ConsignmentContentsSidePanel
